import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sync status types
export const SYNC_STATUS = {
  LIVE: 'live',
  SYNCING: 'syncing',
  DISCONNECTED: 'disconnected'
};

const SyncContext = createContext(null);

export const useSyncManager = () => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSyncManager must be used within SyncProvider');
  }
  return context;
};

export const SyncProvider = ({ children }) => {
  const [syncStatus, setSyncStatus] = useState(SYNC_STATUS.DISCONNECTED);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [driversPresence, setDriversPresence] = useState({});
  const [dashboardSnapshot, setDashboardSnapshot] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const resyncIntervalRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  
  // Get token
  const getToken = () => localStorage.getItem('token');
  
  // Fetch snapshot from server
  const fetchSnapshot = useCallback(async (endpoint) => {
    const token = getToken();
    if (!token) return null;
    
    try {
      setSyncStatus(SYNC_STATUS.SYNCING);
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLastSyncTime(new Date());
        if (data.server_time) {
          setServerTime(new Date(data.server_time));
        }
        return data;
      }
    } catch (error) {
      console.error('Snapshot fetch error:', error);
    }
    return null;
  }, []);
  
  // Fetch drivers presence snapshot
  const fetchDriversPresence = useCallback(async () => {
    const data = await fetchSnapshot('/api/snapshot/drivers-presence');
    if (data) {
      const presenceMap = {};
      data.drivers.forEach(d => {
        presenceMap[d.id] = {
          status: d.status,
          isConnected: d.is_connected,
          lastSeen: d.last_seen,
          position: d.position
        };
      });
      setDriversPresence(presenceMap);
      return data;
    }
    return null;
  }, [fetchSnapshot]);
  
  // Fetch admin dashboard snapshot
  const fetchDashboardSnapshot = useCallback(async () => {
    const data = await fetchSnapshot('/api/snapshot/admin-dashboard');
    if (data) {
      setDashboardSnapshot(data);
      return data;
    }
    return null;
  }, [fetchSnapshot]);
  
  // Get real driver status (from sync state)
  const getDriverStatus = useCallback((driverId) => {
    return driversPresence[driverId]?.status || 'offline';
  }, [driversPresence]);
  
  // Check if driver is truly online
  const isDriverOnline = useCallback((driverId) => {
    return driversPresence[driverId]?.isConnected || false;
  }, [driversPresence]);
  
  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    const token = getToken();
    if (!token) return;
    
    // Determine user type from token
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userType = payload.user_type === 'admin' ? 'dispatcher' : payload.user_type;
      
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // Remove protocol and /api suffix from API_URL
      const wsHost = API_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const wsUrl = `${wsProtocol}//${wsHost}/ws/${userType}`;
      
      console.log('SyncManager: Connecting to WebSocket...', wsUrl);
      
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('SyncManager: WebSocket opened, authenticating...');
        wsRef.current.send(JSON.stringify({ token }));
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'authenticated') {
            console.log('SyncManager: WebSocket authenticated');
            setSyncStatus(SYNC_STATUS.LIVE);
            reconnectAttempts.current = 0;
            setLastSyncTime(new Date());
            
            // Clear polling if was in disconnected mode
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            
            // Initial sync after connection
            fetchDriversPresence();
            fetchDashboardSnapshot();
          }
          
          // Handle presence updates
          else if (data.type === 'driver.presence.updated') {
            setDriversPresence(prev => ({
              ...prev,
              [data.driver_id]: {
                ...prev[data.driver_id],
                status: data.status,
                lastSeen: data.last_seen,
                isConnected: data.status === 'online'
              }
            }));
          }
          
          // Handle location updates
          else if (
            data.type === 'driver.location.updated' ||
            data.type === 'driver_location_updated' ||
            data.type === 'driver_moved' ||
            data.type === 'driver_location'
          ) {
            setDriversPresence(prev => ({
              ...prev,
              [data.driver_id]: {
                ...prev[data.driver_id],
                position: data.position
              }
            }));
          }
          
          // Handle metrics updates
          else if (data.type === 'dashboard.metrics.updated') {
            // Trigger dashboard refresh
            fetchDashboardSnapshot();
          }
          
          // Handle trip events
          else if (data.type.startsWith('trip.')) {
            // Trigger relevant refreshes
            fetchDashboardSnapshot();
          }
          
        } catch (e) {
          console.error('SyncManager: Error parsing message', e);
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('SyncManager: WebSocket closed', event.code);
        setSyncStatus(SYNC_STATUS.DISCONNECTED);
        
        // Attempt reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          console.log(`SyncManager: Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else {
          // Fall back to polling
          startPolling();
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('SyncManager: WebSocket error', error);
      };
      
    } catch (e) {
      console.error('SyncManager: Error connecting WebSocket', e);
    }
  }, [fetchDriversPresence, fetchDashboardSnapshot]);
  
  // Start polling mode (fallback when WS disconnected)
  const startPolling = useCallback(() => {
    console.log('SyncManager: Starting polling mode');
    setSyncStatus(SYNC_STATUS.DISCONNECTED);
    
    if (pollIntervalRef.current) return;
    
    pollIntervalRef.current = setInterval(() => {
      fetchDriversPresence();
      fetchDashboardSnapshot();
    }, 10000); // Poll every 10 seconds
  }, [fetchDriversPresence, fetchDashboardSnapshot]);
  
  // Force resync
  const forceResync = useCallback(async () => {
    console.log('SyncManager: Force resync triggered');
    setSyncStatus(SYNC_STATUS.SYNCING);
    
    await Promise.all([
      fetchDriversPresence(),
      fetchDashboardSnapshot()
    ]);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      setSyncStatus(SYNC_STATUS.LIVE);
    }
  }, [fetchDriversPresence, fetchDashboardSnapshot]);
  
  // Initialize
  useEffect(() => {
    const token = getToken();
    if (token) {
      connectWebSocket();
      
      // Setup periodic resync every 3 minutes
      resyncIntervalRef.current = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          console.log('SyncManager: Periodic resync');
          fetchDriversPresence();
        }
      }, 180000); // 3 minutes
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (resyncIntervalRef.current) {
        clearInterval(resyncIntervalRef.current);
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [connectWebSocket, fetchDriversPresence]);
  
  // Reconnect when token changes (login/logout)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = getToken();
      if (token) {
        connectWebSocket();
      } else if (wsRef.current) {
        wsRef.current.close();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connectWebSocket]);
  
  const value = {
    syncStatus,
    lastSyncTime,
    serverTime,
    driversPresence,
    dashboardSnapshot,
    getDriverStatus,
    isDriverOnline,
    forceResync,
    fetchDriversPresence,
    fetchDashboardSnapshot,
    fetchSnapshot
  };
  
  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
};

export default SyncContext;
